import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '@/api/endpoints';

interface OnlineState {
  /** True when the pack house answered recently. Browser "online" alone is not trusted. */
  online: boolean;
  /** Probe the API now and update. Resolves with the new state. */
  check: () => Promise<boolean>;
}

const Ctx = createContext<OnlineState>({ online: true, check: async () => true });

const PROBE_INTERVAL_MS = 30_000;

/**
 * Connection state as the grower experiences it: can this phone reach Fruitways right now?
 * Listens to the browser events, and confirms with a cheap health probe on a timer and on
 * every reconnect, because rural signal often reports "online" with no working route.
 */
export function OnlineProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const probing = useRef<Promise<boolean> | null>(null);

  const check = useCallback(async () => {
    if (!probing.current) {
      probing.current = (async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          setOnline(false);
          return false;
        }
        try {
          await api.health();
          setOnline(true);
          return true;
        } catch {
          setOnline(false);
          return false;
        } finally {
          probing.current = null;
        }
      })();
    }
    return probing.current;
  }, []);

  useEffect(() => {
    const up = () => void check();
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void check();
    });
    const t = setInterval(() => void check(), PROBE_INTERVAL_MS);
    void check();
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      clearInterval(t);
    };
  }, [check]);

  const value = useMemo(() => ({ online, check }), [online, check]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOnline(): OnlineState {
  return useContext(Ctx);
}
